import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/** The page translator, as the two plain helpers below take it. */
type Translate = ReturnType<typeof useTranslation<"team">>["t"];
import { Alert, Button, Card, CardHeader, ConfirmPrompt, Field, Input } from "../components/ui";
import { PasswordInput } from "../components/AuthShell";
import { Modal } from "../components/Modal";
import { ApiError } from "../lib/api";
import { useCancelOrgClosure, useCloseOrg } from "../lib/queries";
import type { Organization } from "../lib/types";

const MICROS_PER_UNIT = 1_000_000;

function money(micros: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
      micros / MICROS_PER_UNIT,
    );
  } catch {
    return `${(micros / MICROS_PER_UNIT).toFixed(2)} ${currency}`;
  }
}

/**
 * The exact moment, with its zone.
 *
 * The zone is not decoration. A deletion time reading "7:00 PM" tells somebody
 * in another country almost nothing, and this is the one timestamp on the
 * platform where being a few hours out about it costs a database.
 */
function exactTime(iso: string): string {
  /*
   * Component options rather than `dateStyle`/`timeStyle`. Intl refuses to
   * combine either of those with `timeZoneName` and throws "Invalid option" —
   * which, in a card that only renders once a closure is booked, would have
   * been a blank screen for exactly the customers who most need to read it.
   */
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/**
 * How long is left, as a countdown rather than a relative timestamp.
 *
 * `relativeTime` is the wrong tool here and it took rendering the card to see
 * it. At exactly seven days it says "next week", which is vague about a
 * deadline that deletes databases; and once the date has passed — the sweep
 * runs every five minutes, so there is always a small window — it says "1 hour
 * ago", which reads as though the deletion already happened while the customer
 * is looking at a page that still offers to call it off.
 */
function closingIn(iso: string, t: Translate): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return t("close.soon");
  if (ms <= 0) return t("close.imminent");

  const hours = ms / 3_600_000;
  if (hours < 1) return t("close.underHour");

  /*
   * Rounded before the branch, not after. Choosing the unit from the raw value
   * puts 23.99 hours in the hours branch and then rounds it to "in 24 hours" —
   * which is silly on its own and unstable besides, since a closure booked
   * exactly a day out lands on either side depending on how many milliseconds
   * have passed since.
   */
  const wholeHours = Math.round(hours);
  if (wholeHours < 24) return t("close.hours", { count: wholeHours });

  const days = Math.round(hours / 24);
  return t("close.days", { count: days });
}

/**
 * "3 applications and 1 database", or null when there is nothing to name.
 *
 * Both halves keyed: the plural is per language, and so is the conjunction
 * joining them — " and " is an English decision that this used to make with a
 * string literal.
 */
function atStake(apps: number | null, databases: number | null, t: Translate): string | null {
  if (apps === null || databases === null) return null;
  const parts: string[] = [];
  if (apps > 0) parts.push(t("close.apps", { count: apps }));
  if (databases > 0) parts.push(t("close.databases", { count: databases }));
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0] as string;
  return t("close.stakePair", { first: parts[0], last: parts[1] });
}

/**
 * Closing the organization, and calling it off.
 *
 * §9 of the Terms has told customers to "ask us from the dashboard" since it
 * was published, and there was no such control — closing an account meant
 * sending an email and hoping.
 *
 * Three properties this card exists to hold.
 *
 * **It says what goes before it asks for anything.** The list is not fine
 * print: applications and their containers, databases and their data, backups,
 * and the deploy keys we installed on repositories the customer keeps after
 * they leave. A confirmation that says "are you sure?" over an unnamed set of
 * consequences is not consent — so the resting card names what this particular
 * organization stands to lose, in its own numbers, before the dialog opens.
 *
 * **The balance is named as a figure, not a caveat.** Credit is forfeited on
 * closure, and "credit is non-refundable" in a legal page is not the same as
 * seeing "$42.18 will be forfeited" a moment before typing your password.
 *
 * **The scheduled state leads with the deadline.** It is the only fact that
 * matters once a closure is booked, and the first draft of this card had it as
 * grey subtext under a heading — the least prominent thing in a card about it.
 */
export function CloseOrgCard({
  org,
  balanceMicros,
  currency,
  isOwner,
  appCount = null,
  databaseCount = null,
  onChanged,
}: {
  org: Organization;
  /** Null while billing is still loading — the figure is not guessed at. */
  balanceMicros: number | null;
  currency: string;
  isOwner: boolean;
  /** Null while still loading, for the same reason. */
  appCount?: number | null;
  databaseCount?: number | null;
  /**
   * Reloads whatever holds the organization this card was handed.
   *
   * The dashboard keeps it in the cached session rather than in a query, so
   * invalidating React Query does not move it — without this the card stays on
   * whichever state it was in until a full page reload, which is exactly the
   * moment a customer needs to see that what they asked for happened. Taken as
   * a callback rather than read from context here so the card stays
   * presentational and testable on its own.
   */
  onChanged?: () => Promise<void> | void;
}) {
  const { t } = useTranslation("team");
  const close = useCloseOrg(org.id);
  const cancel = useCancelOrgClosure(org.id);

  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const nameMatches = confirmName.trim().toLowerCase() === org.name.trim().toLowerCase();
  const stake = atStake(appCount, databaseCount, t);

  function reset() {
    setPassword("");
    setConfirmName("");
    setError(null);
  }

  async function submit() {
    setError(null);
    if (!nameMatches) {
      setError(t("close.typeName"));
      return;
    }
    if (!password) {
      setError(t("close.typePassword"));
      return;
    }

    try {
      await close.mutateAsync({ password, confirmName: confirmName.trim() });
      await onChanged?.();
      setOpen(false);
      reset();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("close.scheduleFailed"));
    }
  }

  async function onCancel() {
    setError(null);
    try {
      await cancel.mutateAsync();
      await onChanged?.();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("close.cancelFailed"));
    }
  }

  /*
   * The scheduled state replaces the card rather than sitting beside it. There
   * is exactly one useful action once a closure is pending, and offering the
   * other one next to it would be offering to schedule a closure twice.
   *
   * Tinted rather than merely outlined, unlike the resting card below: a tenant
   * counting down to deletion should not look like a tenant that is fine.
   */
  if (org.closesAt) {
    return (
      <Card className="border-danger/40 bg-danger/5">
        <div className="space-y-4 p-5">
          {error && <Alert>{error}</Alert>}

          {/* The deadline first, and as the largest thing here. It is the only
              fact that matters once a closure is booked. */}
          <div>
            <p className="micro text-danger">{t("close.closing")}</p>
            <p className="mt-1.5 text-xl font-semibold text-fg">{closingIn(org.closesAt, t)}</p>
            <p className="mt-0.5 text-sm text-fg-muted tabular-nums">{exactTime(org.closesAt)}</p>
          </div>

          <p className="text-sm text-fg-muted">
            <Trans
              ns="team"
              i18nKey="close.scheduled"
              components={{ safe: <span className="text-fg" /> }}
            />
          </p>

          {isOwner ? (
            <Button loading={cancel.isPending} onClick={() => void onCancel()}>
              {t("close.keep")}
            </Button>
          ) : (
            <p className="text-sm text-fg-subtle">
              {t("close.askOwner")}
            </p>
          )}
        </div>
      </Card>
    );
  }

  if (!isOwner) return null;

  return (
    /*
     * Ordinary border at rest. The first draft outlined this in danger red, so
     * the Team page carried a permanent alarm about a control nobody had
     * touched — the colour belongs on the action, not on the furniture.
     */
    <Card>
      <CardHeader
        title={t("close.title")}
        description={t("close.description")}
      />
      <div className="space-y-4 p-5">
        {error && !open && <Alert>{error}</Alert>}

        <div className="space-y-1.5 text-sm text-fg-muted">
          {/* The stake in this organization's own numbers. A generic warning is
              easy to skim; "3 applications and 1 database" is not. */}
          {stake && (
            <p>
              <Trans
                ns="team"
                i18nKey="close.stake"
                values={{ stake }}
                components={{ what: <span className="text-fg" /> }}
              />
            </p>
          )}
          <p>{t("close.unaffected")}</p>
        </div>

        <Button variant="danger" onClick={() => setOpen(true)}>
          {t("close.open")}
        </Button>
      </div>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          reset();
        }}
        title={t("close.modalTitle", { organization: org.name })}
        description={t("close.modalDescription")}
      >
        <div className="space-y-4">
          {error && <Alert>{error}</Alert>}

          <div className="space-y-1.5 rounded-md border border-danger/30 bg-danger/5 px-3.5 py-3 text-xs text-fg-muted">
            <p className="font-medium text-fg">{t("close.deletedTitle")}</p>
            <ul className="list-disc space-y-0.5 pl-4">
              {(t("close.deleted", { returnObjects: true }) as string[]).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="pt-1">{t("close.irreversible")}</p>
          </div>

          {/* The figure, not a caveat about figures. */}
          {balanceMicros !== null && balanceMicros > 0 && (
            <Alert tone="warning">
              {t("close.forfeit", { amount: money(balanceMicros, currency) })}
            </Alert>
          )}

          <Field
            label={t("close.confirmName")}
            htmlFor="close-confirm-name"
            hint={
              <ConfirmPrompt phrase={org.name} sentence={(slot) => t("close.confirmNameHint", { organization: slot })} />
            }
            error={confirmName.length > 0 && !nameMatches ? t("close.confirmNameError") : null}
          >
            {/*
              No placeholder, deliberately. A placeholder holding the exact
              string being asked for is the answer printed on the question: it
              reads as a pre-filled field at a glance, and it turns a check that
              somebody knows which organization this is into a copying exercise.
              The hint below carries the name for anyone who needs to read it.
            */}
            <Input
              id="close-confirm-name"
              value={confirmName}
              autoComplete="off"
              aria-invalid={confirmName.length > 0 && !nameMatches ? true : undefined}
              onChange={(event) => setConfirmName(event.target.value)}
            />
          </Field>

          <Field
            label={t("close.password")}
            htmlFor="close-password"
            hint={t("close.passwordHint")}
          >
            <PasswordInput
              id="close-password"
              size="md"
              surface="panel"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              disabled={close.isPending}
              onClick={() => {
                setOpen(false);
                reset();
              }}
            >
              {t("close.cancel")}
            </Button>
            <Button variant="danger" loading={close.isPending} onClick={() => void submit()}>
              {t("close.submit")}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
